import { Component, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { TimeLineItem } from 'src/app/shared/models/timeline-item/timeline-item';
import { ConsolidatedTransaction } from 'src/app/shared/models/transaction/consolidated-transaction';
import { TransactionGroup } from 'src/app/shared/models/transaction/transaction-group';

@Component({
  selector: 'app-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})

/**
 * Displays a timeline of month-expansion-panels
 */
export class TimelineComponent implements OnDestroy {
  timelineItems?: TimeLineItem[] = [];
  monthsStrings: { [key0: number]: string } = {};
  months: { [key0: number]: { [key0: number]: TimeLineItem } } = {};
  days: { [key0: number]: { [key0: number]: TimeLineItem } } = {};
  private page = 0;
  private pageSize = 40;
  loading = true;
  private subsink = new Subscription();
  private _filters: any = {};
  private apiCall?: Subscription;

  /**
   * sets filters to some value
   * @param {any} value
   */
  @Input()
  set filters(value: any) {
    this._filters = value;
    if (!this._filters.startDate) return;
    this.resetTimeline();
    this.fetchTransactions();
  }

  /**
   * gets filters
   */
  get filters() {
    return this._filters;
  }

  /**
   * @constructor
   */
  constructor(
    public readonly rewardService: RewardService,
    public readonly translationService: TranslationService
  ) {}

  /**
   * cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * @param {number} month
   * @return {Promise<string>}
   */
  async translateMonth(month: number): Promise<string> {
    return this.translationService.translateString(`month-${month}`);
  }

  /**
   * @param {number} month
   * @return {string}
   */
  getMonthString(month: number): string {
    if (this.monthsStrings[month] == null) {
      this.translateMonth(month).then((translation) => {
        this.monthsStrings[month] = translation;
      });
      return '';
    }
    return this.monthsStrings[month];
  }

  /**
   * Pull paginated transactions
   */
  fetchTransactions() {
    this.apiCall?.unsubscribe();
    this.loading = true;
    const params = {
      page: this.page,
      pageSize: this.pageSize,
      familyId: this._filters.series[0],
    };

    this.assignParams(params);

    const transactions$ = this.rewardService
      .getTransactions(params)
      .subscribe((groups) => {
        if (groups.length > 0) {
          this.page++;
        }
        groups.forEach((group) => {
          const day = group.date.getDate();
          const month = group.date.getMonth();
          this.days[month] = this.days[month] || {};
          if (this.days[month][day] == null) {
            this.days[month][day] = new TimeLineItem(group.date, []);
            this.timelineItems!.push(this.days[month][day]);
          }
          const consolidatedList = this.convertGroupToConsolidated(group);
          consolidatedList.forEach((consolidated) => {
            this.days[month][day].transactions.push(consolidated);
          });
        });
      });
    transactions$.add(() => {
      this.loading = false;
    });
    this.apiCall = transactions$;
    this.subsink.add(transactions$);
  }

  /**
   * Resets timeline
   */
  resetTimeline() {
    this.timelineItems = [];
    this.page = 0;
    this.days = {};
  }

  /**
   * Assign filter params to be fetched
   * @param {{}} params
   */
  assignParams(params: {}) {
    if (
      this._filters.added === false &&
      this._filters.spend === false &&
      this._filters.expired === false
    ) {
      if (this._filters.startDate) {
        Object.assign(params, {
          startDate: this._filters.startDate,
          endDate: this._filters.endDate,
        });
      } else {
        Object.assign(params);
      }
    } else {
      Object.assign(params, this.filters);
    }
  }

  /**
   * Convert Transaction group to consolidated
   * @param {TransactionGroup} group
   * @return {ConsolidatedTransaction[]}
   */
  convertGroupToConsolidated(
    group: TransactionGroup
  ): ConsolidatedTransaction[] {
    const grouped: { [key: string]: ConsolidatedTransaction } = {};

    group.transactions.forEach((transaction) => {
      const key = transaction.transactionType;
      if (!grouped[key]) {
        grouped[key] = new ConsolidatedTransaction(
          transaction.id,
          transaction.totalPoints,
          transaction.transactionType,
          group.date,
          transaction.expirationDate,
          transaction.userName,
          transaction.transactionId,
          [transaction]
        );
      } else {
        if (transaction.expirationDate! < grouped[key].expirationDate!) {
          grouped[key].expirationDate = transaction.expirationDate;
        }
        grouped[key].totalPoints += transaction.totalPoints;
        grouped[key].transactions.push(transaction);
      }
    });
    return Object.values(grouped);
  }
}
